import { useCallback, useRef, useMemo, useState } from 'react';

import { PlusIcon, ZapIcon, LanguagesIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useTitle } from 'react-use';

import ContextualHelp from '@/components/atoms/ContextualHelp/ContextualHelp';
import { DetailsPanelToggle } from '@/components/atoms/DetailsPanelToggle/DetailsPanelToggle';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { ReadOnlyBanner } from '@/components/atoms/ReadOnlyBanner/ReadOnlyBanner';
import SearchBar from '@/components/atoms/SearchBar/SearchBar';
import { MODAL_AI_AGENT_CREATE } from '@/components/organisms/Modals/ModalConductor';
import {
  PageSubHeader,
  PageSubHeaderActions,
  PageSubHeaderBreadcrumbs,
} from '@/components/organisms/PageSubHeader';
import { Sidebar } from '@/components/organisms/Sidebar/Sidebar';
import { SidebarLayout } from '@/components/organisms/Sidebar/SidebarLayout';
import PermissionsWrapper from '@/components/templates/PermissionsWrapper';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import {
  ComplexFilterBar,
  SavedFilterView,
} from '@/components/ui/custom/ComplexFilter/ComplexFilterBar';
import {
  ComplexFilterCombobox,
  FilterDefinition,
  SavedFilterItem,
} from '@/components/ui/custom/ComplexFilter/ComplexFilterCombobox';
import { EmptySkeleton } from '@/components/ui/custom/EmptySkeleton';
import { ColumnVisibilityMenu } from '@/components/ui/table/column-visibility-menu';
import { HiddenColumnsIndicator } from '@/components/ui/table/hidden-columns-indicator';
import { useColumnVisibility } from '@/components/ui/table/useColumnVisibility';
import useBrains from '@/hooks/useBrains';
import { useEmptyStateView } from '@/hooks/useEmptyStateView';
import { useSetDefaultSidebar } from '@/hooks/useSetDefaultSidebar';
import { useSidebar } from '@/hooks/useSidebar';
import { getFilterValues, isFilterExcluded } from '@/hooks/useTableFilters';
import { useUrlPagination } from '@/hooks/useUrlTablePagination';
import { pushModal } from '@/redux/modals/actions';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountSlug, selectBrainId } from '@/redux/session/selectors';
import { trackEvent } from '@/segment/segment';
import useSegments from '@/segment/useSegments';
import { AIAgent, BrainType } from '@/shared/types/brain';
import { actions } from '@/shared/types/permissions';
import { EventName, PageName } from '@/shared/types/segment';
import { RootState } from '@/shared/types/state';
import { AI_AGENTS_DOCS_URL, TABLE_LAYOUT_FILL_CLASSES } from '@/util/constants';
import { greekAwareTextMatch } from '@/util/greekFilter';
import { formatLanguageLabel, prioritizeLanguageOptions } from '@/util/languageOptions';

const BRAIN_TYPES = [
  BrainType.EARLY_ENGAGEMENT,
  BrainType.ACTIVATION,
  BrainType.UPSELL,
  BrainType.DEBT_COLLECTION,
  BrainType.SUPPORT,
] as const;

import { AIAgentsTable } from './components/AIAgentsTable/AIAgentsTable';
import { columns } from './components/AIAgentsTable/columns';
import { EmptyAIAgents } from './components/EmptyAIAgents';
import { AIAgentForm } from './components/RightPanel/AIAgentForm';
import { AIAgentsDetails } from './components/RightPanel/AIAgentsDetails';
import { getLastVisitedCategory } from './helper';
import { useAgentFavorites } from './hooks/useAgentFavorites';
import { useAIAgentsFilters } from './hooks/useAIAgentsFilters';

export default function AIAgents() {
  // Redux selectors
  const slug = useSelector(selectAccountSlug);
  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'brains', actions.WRITE)
  );
  useSetDefaultSidebar('details');
  const { isOpen, open: openPanel, close: closePanel } = useSidebar();

  // Local states
  const [searchTerm, setSearchTerm] = useState('');
  const [openPillId, setOpenPillId] = useState<string | null>(null);

  const brainId = useSelector(selectBrainId);
  const isDetailsSidebarOpen = isOpen('details');
  const isEditAIAgentSidebarOpen = isOpen('edit_ai_agent');

  // Custom hooks
  const { t } = useTranslation();
  useTitle(t('pages.ai_agents'));
  const dispatch = useDispatch();
  const { brains: aiAgents, maxBrainsReached: maxAIAgentsReached, listStatus } = useBrains();

  // URL-based pagination for preserving page state across navigation
  const { pagination, setPagination } = useUrlPagination();
  const handlePageChange = useCallback(
    (pageIndex: number) => {
      setPagination({ ...pagination, pageIndex });
    },
    [pagination, setPagination]
  );

  // Filters
  const {
    filters,
    filteredAIAgents,
    activeFilterCount,
    setFilter,
    removeFilter,
    resetFilters,
    savedViews,
    activeView,
    hasUnsavedChanges,
    saveView,
    updateView,
    deleteView,
    renameView,
    applyView,
    clearActiveView,
  } = useAIAgentsFilters(aiAgents);

  // Available languages for filter dropdown
  const availableLanguages = useMemo(() => {
    if (!aiAgents?.length) {
      return [];
    }
    return [...new Set(aiAgents.map((agent) => agent.language))];
  }, [aiAgents]);

  // Filter definitions for the generic filter dropdown
  const filterDefinitions = useMemo((): FilterDefinition[] => {
    // Type filter options
    const typeOptions = BRAIN_TYPES.map((type) => ({
      label: t(`ai_agents.types.${type.toLowerCase()}.title`),
      value: type,
    }));

    // Language filter options with native names
    const languageOptions = prioritizeLanguageOptions(
      availableLanguages.map((lang) => ({ label: formatLanguageLabel(lang), value: lang }))
    );

    return [
      {
        id: 'types',
        label: t('ai_agents.filters.type'),
        icon: ZapIcon,
        type: 'checkbox',
        options: typeOptions,
      },
      {
        id: 'languages',
        label: t('ai_agents.filters.language'),
        icon: LanguagesIcon,
        type: 'checkbox',
        options: languageOptions,
        condition: true,
      },
    ];
  }, [t, availableLanguages]);

  // Filter badges for status bar
  const filterBadges = useMemo(() => {
    const badges = [];

    const types = getFilterValues(filters, 'types') as BrainType[];
    const languages = getFilterValues(filters, 'languages');

    // Type filter badge
    if (types.length > 0) {
      const typesDef = filterDefinitions.find((f) => f.id === 'types')!;
      badges.push({
        id: 'types',
        icon: ZapIcon,
        title: t('ai_agents.filters.type'),
        text:
          types.length === 1
            ? t(`ai_agents.types.${types[0].toLowerCase()}.title`)
            : `${t(`ai_agents.types.${types[0].toLowerCase()}.title`)} +${types.length - 1}`,
        onRemove: () => removeFilter('types'),
        filterDefinition: {
          overlayLabel: typesDef.overlayLabel,
          type: typesDef.type,
          options: typesDef.options,
        },
        value: types,
        onValueChange: (value: string | string[] | undefined) =>
          value && (value as string[]).length > 0
            ? setFilter('types', value as BrainType[])
            : removeFilter('types'),
      });
    }

    // Language filter badge
    if (languages.length > 0) {
      const languagesDef = filterDefinitions.find((f) => f.id === 'languages')!;
      const languageExcluded = isFilterExcluded(filters, 'languages');
      const conditionText = languageExcluded ? t('common.is_not') : t('common.is');
      const languageName = formatLanguageLabel(languages[0]);

      badges.push({
        id: 'languages',
        icon: LanguagesIcon,
        title: t('ai_agents.filters.language'),
        text:
          languages.length === 1
            ? `${conditionText} ${languageName}`
            : `${conditionText} ${languageName} +${languages.length - 1}`,
        onRemove: () => removeFilter('languages'),
        filterDefinition: {
          overlayLabel: languagesDef.overlayLabel,
          type: languagesDef.type,
          options: languagesDef.options,
          condition: languagesDef.condition,
        },
        value: languages,
        onValueChange: (value: string | string[] | undefined, exclude?: boolean) =>
          value && (value as string[]).length > 0
            ? setFilter('languages', value as string[], exclude)
            : removeFilter('languages'),
        exclude: languageExcluded,
      });
    }

    return badges;
  }, [filters, filterDefinitions, removeFilter, setFilter, t]);

  // Map saved views to dropdown format
  const savedFilterItems: SavedFilterItem[] = useMemo(
    () => savedViews.map((f) => ({ id: f.id, name: f.name })),
    [savedViews]
  );

  // Map active view to status bar format
  const activeSavedFilterView: SavedFilterView | null = useMemo(
    () => (activeView ? { id: activeView.id, name: activeView.name } : null),
    [activeView]
  );

  const { favoriteIds, toggleFavorite } = useAgentFavorites();

  const tableColumns = useMemo(
    () => columns({ toggleFavorite, favoriteIds }),
    [toggleFavorite, favoriteIds]
  );

  const {
    columnVisibility,
    setColumnVisibility,
    resetColumnVisibility,
    hideableColumns,
    hasHiddenColumns,
  } = useColumnVisibility(tableColumns, 'aiAgentsColumnVisibility');

  // Controlled state for column customization dropdown
  const [isCustomizeColumnsOpen, setIsCustomizeColumnsOpen] = useState(false);

  // Button ref will be used to prevent the popover modal from closing when you click out
  const customizeButtonRef = useRef<HTMLButtonElement>(null);

  // Hidden columns indicator for pagination
  const hiddenColumnsIndicator = hasHiddenColumns ? (
    <HiddenColumnsIndicator
      onCustomize={() => setIsCustomizeColumnsOpen((prev) => !prev)}
      buttonRef={customizeButtonRef}
    />
  ) : undefined;

  // Displayed AI agents based on search term and filters.
  // Pinned agents are sorted to the top of the list.
  const displayedAgents = useMemo(() => {
    if (!filteredAIAgents) {
      return [];
    }

    let agents = filteredAIAgents;
    if (searchTerm) {
      agents = filteredAIAgents.filter((agent) => greekAwareTextMatch(agent.name, searchTerm));
    }

    // Sort pinned agents to the top
    return [...agents].sort((a, b) => {
      const aIsPinned = favoriteIds.includes(a.brain_id);
      const bIsPinned = favoriteIds.includes(b.brain_id);
      if (aIsPinned && !bIsPinned) {
        return -1;
      }
      if (!aIsPinned && bIsPinned) {
        return 1;
      }
      return 0;
    });
  }, [filteredAIAgents, searchTerm, favoriteIds]);

  // Local variables
  const disabled = maxAIAgentsReached || !canWrite;
  const isLoading = listStatus === 'pending';
  const { showEmpty, showEmptySkeleton, showList, isEmptyPage } = useEmptyStateView({
    scope: 'ai-agents',
    isLoading,
    isEmpty:
      displayedAgents.length === 0 &&
      listStatus === 'success' &&
      searchTerm === '' &&
      activeFilterCount === 0,
  });

  // Track page view analytics on component mount.
  useSegments(PageName.AI_AGENTS);

  // Handle create AI agent button click.
  const handleButtonClick = useCallback(() => {
    trackEvent(EventName.ClickCreateBrain);
    dispatch(pushModal(MODAL_AI_AGENT_CREATE));
  }, [dispatch]);

  // Generate href for AI agent table rows to enable opening in new tabs.
  const getRowHref = useCallback(
    (row: AIAgent) => {
      const isTeamOnboardingAccount = slug === 'team-onboarding';

      // Don't provide href if onboarding and not team-onboarding account
      if (row.metadata?.onboarding_status === 'unavailable' && !isTeamOnboardingAccount) {
        return null;
      }

      const lastCategory = getLastVisitedCategory(row.brain_id);
      return `/${slug}/ai-agents/${row.brain_id}/${lastCategory}`;
    },
    [slug]
  );

  // Get tooltip message for disabled create button
  const getDisabledButtonTooltip = useCallback(() => {
    if (maxAIAgentsReached) {
      return t('ai_agents.create_button.max_agents_reached');
    }
    if (!canWrite) {
      return t('ai_agents.create_button.no_permission');
    }
    return '';
  }, [maxAIAgentsReached, canWrite, t]);

  const detailsTooltipContent = isDetailsSidebarOpen
    ? t('common.hide_details')
    : t('common.show_details');

  return (
    <PermissionsWrapper module="brains">
      <SidebarLayout
        autoSaveId="ai-agents-sidebar"
        sidebar={
          <Sidebar
            when={['details', 'edit_ai_agent']}
            title={t(isEditAIAgentSidebarOpen ? 'ai_agents.edit_ai_agent' : 'common.details')}
          >
            {isEditAIAgentSidebarOpen && <AIAgentForm />}
            {isDetailsSidebarOpen && <AIAgentsDetails />}
          </Sidebar>
        }
      >
        {/* Page sub header */}
        <PageSubHeader
          breadcrumbs={
            <PageSubHeaderBreadcrumbs>
              <Breadcrumb>
                <BreadcrumbList>
                  <BreadcrumbItem>
                    <BreadcrumbPage>
                      <div className="flex h-8 w-8 items-center justify-center">
                        <ZapIcon size={16} className="text-muted-foreground" />
                      </div>
                      <div className="mr-4 ml-2 h-4 w-px bg-border" />
                      <span className="text-sm leading-5 font-normal text-foreground">
                        {t('common.ai_agents')}
                      </span>
                    </BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>
            </PageSubHeaderBreadcrumbs>
          }
          actions={
            <PageSubHeaderActions>
              <div className="flex items-center gap-2">
                <DetailsPanelToggle
                  pressed={isDetailsSidebarOpen}
                  onPressedChange={(pressed) => (pressed ? openPanel('details') : closePanel())}
                  tooltip={detailsTooltipContent}
                />
                {!isEmptyPage &&
                  (disabled ? (
                    <InfoTooltip content={getDisabledButtonTooltip()}>
                      <span>
                        <Button onClick={handleButtonClick} disabled={disabled} size="sm">
                          <PlusIcon size={16} />
                          {t('common.create')}
                        </Button>
                      </span>
                    </InfoTooltip>
                  ) : (
                    <Button onClick={handleButtonClick} disabled={disabled} size="sm">
                      <PlusIcon size={16} />
                      {t('common.create')}
                    </Button>
                  ))}
              </div>
            </PageSubHeaderActions>
          }
        />

        {!canWrite && <ReadOnlyBanner />}
        <div className={TABLE_LAYOUT_FILL_CLASSES}>
          <ContextualHelp
            title={t('ai_agents.contextual_title')}
            name="about_ai_agents"
            links={[{ label: t('ai_agents.contextual_link'), url: AI_AGENTS_DOCS_URL }]}
          >
            {t('ai_agents.contextual_message')}
          </ContextualHelp>

          {showEmpty && (
            <EmptyAIAgents disabled={disabled} disabledTooltip={getDisabledButtonTooltip()} />
          )}

          {showEmptySkeleton && <EmptySkeleton descriptionLines={3} actions={2} />}

          {showList && (
            <>
              <div className="mb-4 flex items-center justify-between gap-4 pl-1">
                <SearchBar
                  placeholder={t('ai_agents.search_ai_agent')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />

                <div className="flex items-center gap-2">
                  <ComplexFilterCombobox
                    filterDefinitions={filterDefinitions}
                    filters={filters}
                    setFilter={setFilter}
                    removeFilter={removeFilter}
                    activeFilterCount={activeFilterCount}
                    savedFilters={savedFilterItems}
                    onApplySavedFilter={(item) => {
                      const view = savedViews.find((f) => f.id === item.id);
                      if (view) {
                        applyView(view);
                      }
                    }}
                    onOpenActivePill={setOpenPillId}
                  />

                  <ColumnVisibilityMenu
                    columns={hideableColumns}
                    columnVisibility={columnVisibility}
                    onColumnVisibilityChange={setColumnVisibility}
                    onReset={resetColumnVisibility}
                    open={isCustomizeColumnsOpen}
                    onOpenChange={setIsCustomizeColumnsOpen}
                    onInteractOutside={(e) => {
                      if (e.target == customizeButtonRef.current) {
                        e.preventDefault();
                        e.stopPropagation();
                      }
                    }}
                  />
                </div>
              </div>

              <ComplexFilterBar
                className="mb-4"
                filters={filterBadges}
                savedFiltersConfig={{
                  savedFilters: savedFilterItems,
                  activeSavedFilter: activeSavedFilterView,
                  hasUnsavedChanges,
                  onSave: saveView,
                  onUpdate: updateView,
                  onDelete: deleteView,
                  onRename: renameView,
                  onSelectView: (viewId) => {
                    const view = savedViews.find((f) => f.id === viewId);
                    if (view) {
                      applyView(view);
                    }
                  },
                }}
                openPillId={openPillId}
                onOpenPillChange={setOpenPillId}
                onClearAll={() => {
                  resetFilters();
                  clearActiveView();
                }}
              />

              <AIAgentsTable
                className="min-h-0 flex-1"
                data={displayedAgents}
                isLoading={isLoading}
                getRowHref={getRowHref}
                getRowLinkState={() => ({ fromPage: pagination.pageIndex + 1 })}
                columnVisibility={columnVisibility}
                onColumnVisibilityChange={setColumnVisibility}
                initialPageIndex={pagination.pageIndex}
                onPageChange={handlePageChange}
                paginationCenterContent={hiddenColumnsIndicator}
                activeRowId={(isEditAIAgentSidebarOpen && brainId) || undefined}
              />
            </>
          )}
        </div>
      </SidebarLayout>
    </PermissionsWrapper>
  );
}
